/**
 * Publishing Hub
 * Manage Mode A (direct post) + Mode B (SEO Teaser Pipeline).
 * View publishing history, check platform status, trigger manual publishes.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Send, Globe, RefreshCw, CheckCircle, XCircle, Link2,
  Linkedin, Facebook, Twitter, Instagram, Zap, TrendingUp,
  BarChart2, ExternalLink, Eye, Clock, ChevronDown, ChevronUp,
  BookOpen,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = {
  linkedin_personal: Linkedin,
  linkedin_company: Linkedin,
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  iconiqs: Globe,
  qlia: Globe,
};

const PLATFORM_COLORS = {
  linkedin_personal: '#0077b5',
  linkedin_company: '#0a66c2',
  facebook: '#1877f2',
  twitter: '#1da1f2',
  instagram: '#e1306c',
  iconiqs: '#3dffa0',
  qlia: '#a68eff',
};

function StatusDot({ ok }) {
  return (
    <div className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-[#3dffa0]' : 'bg-[#ff5370]'}`} />
  );
}

function PlatformBadge({ name, active }) {
  const Icon = PLATFORM_ICONS[name] || Globe;
  const color = PLATFORM_COLORS[name] || '#a68eff';
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[10px] font-bold"
      style={{
        background: active ? `${color}15` : 'rgba(255,255,255,0.03)',
        border: `1px solid ${active ? color + '35' : 'rgba(255,255,255,0.08)'}`,
        color: active ? color : 'rgba(255,255,255,0.25)',
      }}>
      <Icon size={10} />
      <StatusDot ok={active} />
      {name.replace('_', ' ')}
    </div>
  );
}

function HistoryEntry({ entry }) {
  const [expanded, setExpanded] = useState(false);
  // History is stored as { id, title, mode, result: {...pipeline}, created_at }
  const modeB = entry.result || entry.mode_b || entry;
  const social = modeB.social_posts || {};
  const urls = modeB.published_urls || {};
  const teasers = modeB.teasers || {};

  const socialCount = Object.values(social).filter(Boolean).length;
  const urlCount = Object.values(urls).filter(Boolean).length;

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02] transition-colors"
        onClick={() => setExpanded(s => !s)}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(61,255,160,0.1)', border: '1px solid rgba(61,255,160,0.2)' }}>
          <Send size={12} style={{ color: '#3dffa0' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-white truncate">{entry.title}</div>
          <div className="text-[10px] flex items-center gap-2 mt-0.5"
            style={{ color: 'rgba(255,255,255,0.35)' }}>
            <span>{new Date(entry.created_at).toLocaleString()}</span>
            <span>·</span>
            <span style={{ color: '#3dffa0' }}>{urlCount} site{urlCount !== 1 ? 's' : ''}</span>
            <span>·</span>
            <span style={{ color: '#a68eff' }}>{socialCount} social post{socialCount !== 1 ? 's' : ''}</span>
            <span>·</span>
            <span style={{ color: modeB.destination === 'ai' ? '#3dffa0' : '#a68eff' }}>
              {modeB.destination === 'ai' ? 'iconiqs.ai' : 'qlia.io'}
            </span>
          </div>
        </div>
        {expanded ? <ChevronUp size={13} style={{ color: 'rgba(255,255,255,0.3)' }} /> : <ChevronDown size={13} style={{ color: 'rgba(255,255,255,0.3)' }} />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
          {/* Published URLs */}
          {Object.entries(urls).map(([site, url]) => (
            <div key={site} className="flex items-center gap-2 text-xs">
              <Globe size={11} style={{ color: '#3dffa0' }} />
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>{site}:</span>
              <a href={url} target="_blank" rel="noreferrer"
                className="underline truncate" style={{ color: '#3dffa0' }}>{url}</a>
              <ExternalLink size={10} style={{ color: 'rgba(255,255,255,0.2)' }} />
            </div>
          ))}
          {/* Social post IDs */}
          {Object.entries(social).filter(([, v]) => v).map(([platform, id]) => {
            const Icon = PLATFORM_ICONS[platform] || Globe;
            const color = PLATFORM_COLORS[platform] || '#a68eff';
            return (
              <div key={platform} className="flex items-center gap-2 text-xs">
                <Icon size={11} style={{ color }} />
                <span style={{ color: 'rgba(255,255,255,0.4)' }}>{platform.replace('_', ' ')}:</span>
                <span className="font-mono text-[10px]" style={{ color }}>{id}</span>
              </div>
            );
          })}
          {/* Teasers */}
          {teasers.linkedin && (
            <div className="rounded-lg p-3 text-xs" style={{ background: 'rgba(0,119,181,0.08)', border: '1px solid rgba(0,119,181,0.2)' }}>
              <div className="font-bold mb-1" style={{ color: '#0077b5' }}>LinkedIn Teaser</div>
              <div style={{ color: 'rgba(255,255,255,0.6)' }}>{teasers.linkedin}</div>
            </div>
          )}
          {teasers.facebook && (
            <div className="rounded-lg p-3 text-xs" style={{ background: 'rgba(24,119,242,0.08)', border: '1px solid rgba(24,119,242,0.2)' }}>
              <div className="font-bold mb-1" style={{ color: '#1877f2' }}>Facebook Teaser</div>
              <div style={{ color: 'rgba(255,255,255,0.6)' }}>{teasers.facebook}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PublishingHub() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [testTopic, setTestTopic] = useState('');
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, hRes] = await Promise.all([
        axios.get(`${API}/api/publish/status`).catch(() => ({ data: null })),
        axios.get(`${API}/api/publish/history`).catch(() => ({ data: { history: [] } })),
      ]);
      if (sRes.data) setStatus(sRes.data);
      setHistory(hRes.data?.history || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const testPublish = async () => {
    if (!testTopic.trim()) return;
    setPublishing(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/publish/blog`, {
        title: testTopic,
        content: `This is a test publish for: ${testTopic}. The SEO Teaser Pipeline will classify this content, publish it to the appropriate website (iconiqs.ai for AI content, qlia.io for general), generate curiosity teasers, and post to LinkedIn and Facebook.`,
        post_to_social: true,
      });
      setTestResult(data);
      load();
    } catch (e) {
      setTestResult({ error: e?.response?.data?.detail || 'Failed' });
    } finally {
      setPublishing(false);
    }
  };

  const platforms = status?.platforms || {};
  const websites = status?.websites || {};
  const allPlatforms = { ...platforms, ...websites };
  const activeCount = Object.values(allPlatforms).filter(Boolean).length;

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4"
        style={{ borderBottom: '1px solid var(--llmc-border)', background: 'rgba(10,10,20,0.97)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,rgba(61,255,160,0.2),rgba(0,212,184,0.1))', border: '1px solid rgba(61,255,160,0.3)' }}>
              <Send size={20} style={{ color: '#3dffa0' }} />
            </div>
            <div>
              <div className="font-black text-white text-sm">Publishing Hub</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                Mode A (direct) + Mode B (SEO Teaser → website + LinkedIn + Facebook)
              </div>
            </div>
          </div>
          <button onClick={load} disabled={loading}
            data-testid="publishing-refresh-btn"
            className="p-1.5 rounded-lg transition-all hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--llmc-border)' }}>
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} style={{ color: 'var(--llmc-text-muted)' }} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Platform Status */}
        <div className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-bold text-white">Platform Status</div>
            <div className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.2)' }}>
              {activeCount}/{Object.keys(allPlatforms).length} active
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="platform-status-grid">
            {Object.entries(allPlatforms).map(([name, active]) => (
              <PlatformBadge key={name} name={name} active={active} />
            ))}
            {Object.keys(allPlatforms).length === 0 && (
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Loading platform status...</div>
            )}
          </div>
          {status && (
            <div className="mt-3 flex items-center gap-2 text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
              <Zap size={9} style={{ color: status.teaser_mode ? '#3dffa0' : '#ff5370' }} />
              SEO Teaser Mode: <span style={{ color: status.teaser_mode ? '#3dffa0' : '#ff5370' }}>
                {status.teaser_mode ? 'ON' : 'OFF (set AUTO_SOCIAL_TEASER_MODE=true in Convex)'}
              </span>
            </div>
          )}
        </div>

        {/* Test Pipeline */}
        <div className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
          <div className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <BookOpen size={12} style={{ color: '#f0c27f' }} />
            Test SEO Teaser Pipeline
          </div>
          <div className="flex gap-2">
            <input
              value={testTopic}
              onChange={e => setTestTopic(e.target.value)}
              placeholder="Enter article title to test the pipeline..."
              data-testid="test-topic-input"
              className="flex-1 llmc-input px-3 py-2 text-xs"
              onKeyDown={e => e.key === 'Enter' && testPublish()}
            />
            <button onClick={testPublish} disabled={publishing || !testTopic.trim()}
              data-testid="test-publish-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(61,255,160,0.12)', border: '1px solid rgba(61,255,160,0.3)', color: '#3dffa0' }}>
              {publishing ? <RefreshCw size={11} className="animate-spin" /> : <Send size={11} />}
              {publishing ? 'Publishing...' : 'Test Publish'}
            </button>
          </div>
          {testResult && (
            <div className="mt-3 rounded-xl p-3 text-xs"
              style={{
                background: testResult.error ? 'rgba(255,83,112,0.06)' : 'rgba(61,255,160,0.06)',
                border: `1px solid ${testResult.error ? 'rgba(255,83,112,0.2)' : 'rgba(61,255,160,0.2)'}`,
              }}>
              {testResult.error ? (
                <span style={{ color: '#ff5370' }}>{testResult.error}</span>
              ) : (
                <div className="space-y-1">
                  <div style={{ color: '#3dffa0' }}>Pipeline complete!</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)' }}>
                    Destination: <strong>{testResult.destination || testResult.result?.destination}</strong> ·
                    Social posts: <strong>{Object.values(testResult.social_posts || testResult.result?.social_posts || {}).filter(Boolean).length}</strong>
                  </div>
                  {Object.entries(testResult.published_urls || testResult.result?.published_urls || {}).map(([s, u]) => (
                    <div key={s}><a href={u} target="_blank" rel="noreferrer" className="underline" style={{ color: '#3dffa0' }}>{u}</a></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Publishing History */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold text-white">Publishing History</div>
            <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{history.length} runs</div>
          </div>
          {history.length === 0 ? (
            <div className="rounded-2xl p-8 text-center text-xs"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
              No publishing history yet. Use the test above or generate content with AUTO_SOCIAL_TEASER_MODE enabled.
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((entry, i) => (
                <HistoryEntry key={entry.id || i} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
